import { Wind, CloudRain, RefreshCw, Pause, Play, Zap, Square, Loader2 } from "lucide-react";
import { C, FONT, hexA } from "../theme";
import { NAV } from "./meta";
import { priceLevelColor, priceLevelLabel } from "../lib/price";
import type { PriceInfo, ViewKey, WeatherNow } from "../types";

/** Top header: page title + dateline, weather + power-price chips, global run/stop,
 *  sync, rain-delay toggle, and a mobile nav strip. Wraps below ~700px so the title
 *  is never crushed by the right-hand cluster. */
export function Header({
  view,
  now,
  weather,
  price,
  syncedMinsAgo,
  paused,
  syncing,
  activeCount,
  nextName,
  canRunNext,
  runNextBusy,
  stopAllBusy,
  onRunNext,
  onStopAll,
  onSync,
  onToggleRainDelay,
  onNav,
}: {
  view: ViewKey;
  now: Date;
  weather: WeatherNow;
  price?: PriceInfo;
  syncedMinsAgo: number;
  paused: boolean;
  syncing: boolean;
  activeCount: number;
  nextName: string | null;
  canRunNext: boolean;
  runNextBusy: boolean;
  stopAllBusy: boolean;
  onRunNext: () => void;
  onStopAll: () => void;
  onSync: () => void;
  onToggleRainDelay: () => void;
  onNav: (v: ViewKey) => void;
}) {
  const dateline = `${now.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
  })} · ${now.toTimeString().slice(0, 5)}`;

  const active = activeCount > 0;

  return (
    <>
      <header
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 16,
          minHeight: 90,
          padding: "20px 34px",
          borderBottom: `1px solid ${C.hairline}`,
        }}
      >
        {/* Left: title + dateline */}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: FONT.serif,
              fontSize: 26,
              fontWeight: 500,
              color: C.ink,
              textTransform: "capitalize",
              lineHeight: 1.05,
            }}
          >
            {view}
          </div>
          <div style={{ fontFamily: FONT.mono, fontSize: 12, color: C.mutedDeep, marginTop: 3 }}>
            {dateline}
          </div>
        </div>

        {/* Right: weather + price + controls (wraps under the title on narrow screens) */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", flexWrap: "wrap", gap: 12 }}>
          {/* Weather chip */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              background: C.paper,
              border: `1px solid ${C.line}`,
              borderRadius: "var(--radius-chip)",
              padding: "9px 17px",
            }}
          >
            <span style={{ fontFamily: FONT.serif, fontSize: 19, fontWeight: 500, color: C.ink }}>
              {weather.tempC}°
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontFamily: FONT.mono,
                fontSize: 12,
                color: weather.wind > 7 ? C.beaconDeep : C.textMid,
              }}
            >
              <Wind size={13} />
              {weather.wind} m/s
            </span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                fontFamily: FONT.mono,
                fontSize: 12,
                color: C.textMid,
              }}
            >
              <CloudRain size={13} />
              {weather.rainToday} mm
            </span>
            <span style={{ width: 1, height: 16, background: C.line }} />
            <span style={{ fontSize: 11, color: C.muted }}>
              Open-Meteo · SMHI · {syncedMinsAgo}m ago
            </span>
          </div>

          {/* Power-price reading — compact, like the wind/rain readings (a limit, not a hero). */}
          {price?.enabled && (
            <span
              title={price.ok ? `SE4 spot · ${priceLevelLabel(price.level)} · power gate` : "Nord Pool unavailable — watering on weather alone"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                fontFamily: FONT.mono,
                fontSize: 12,
                color: price.ok ? priceLevelColor(price.level) : C.textLow,
                background: C.paper,
                border: `1px solid ${C.line}`,
                borderRadius: "var(--radius-chip)",
                padding: "9px 13px",
              }}
            >
              <Zap size={13} />
              {price.ok && price.now != null ? `${price.now.toFixed(2)} kr/kWh` : "power n/a"}
              {price.ok && (
                <span
                  style={{
                    fontFamily: FONT.sans,
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: "lowercase",
                    letterSpacing: "0.02em",
                    color: priceLevelColor(price.level),
                    background: hexA(priceLevelColor(price.level), 0.14),
                    borderRadius: 6,
                    padding: "1px 6px",
                  }}
                >
                  {priceLevelLabel(price.level)}
                </span>
              )}
            </span>
          )}

          {/* Global run/stop — contextual: stop what's running, else start the next zone. */}
          {active ? (
            <button
              type="button"
              onClick={onStopAll}
              disabled={stopAllBusy}
              title={`Stop ${activeCount} running ${activeCount === 1 ? "zone" : "zones"}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "9px 14px",
                borderRadius: 12,
                fontSize: 12.5,
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: C.onAccent,
                background: C.coral,
                border: `1px solid ${C.coral}`,
                cursor: stopAllBusy ? "wait" : "pointer",
                opacity: stopAllBusy ? 0.7 : 1,
              }}
            >
              {stopAllBusy ? <Loader2 size={14} className="spin" /> : <Square size={12} fill={C.onAccent} />}
              {activeCount > 1 ? `Stop all (${activeCount})` : "Stop"}
            </button>
          ) : (
            <button
              type="button"
              onClick={onRunNext}
              disabled={!canRunNext || runNextBusy}
              title={canRunNext && nextName ? `Run ${nextName} now` : "No zone ready to run"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                padding: "9px 14px",
                borderRadius: 12,
                fontSize: 12.5,
                fontWeight: 600,
                whiteSpace: "nowrap",
                color: canRunNext ? C.onAccent : C.textLow,
                background: canRunNext ? C.seaDeep : C.track,
                border: `1px solid ${canRunNext ? C.seaDeep : C.hairline}`,
                cursor: !canRunNext ? "not-allowed" : runNextBusy ? "wait" : "pointer",
                opacity: runNextBusy ? 0.7 : 1,
              }}
            >
              {runNextBusy ? <Loader2 size={14} className="spin" /> : <Play size={13} fill={canRunNext ? C.onAccent : "none"} />}
              Run next
            </button>
          )}

          {/* Sync */}
          <button
            type="button"
            onClick={onSync}
            aria-label="Sync weather"
            style={{
              width: 40,
              height: 40,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              borderRadius: 12,
              border: `1px solid ${C.line}`,
              background: C.paper,
              color: C.textMid,
            }}
          >
            <RefreshCw size={16} className={syncing ? "spin" : undefined} />
          </button>

          {/* Rain delay toggle — tinted (no low-contrast white-on-beacon); the solid
              border + Resume icon signal the active state. */}
          <button
            type="button"
            onClick={onToggleRainDelay}
            aria-pressed={paused}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "9px 14px",
              borderRadius: 12,
              fontSize: 12.5,
              fontWeight: 600,
              whiteSpace: "nowrap",
              color: C.beaconDeep,
              background: hexA(C.beacon, paused ? 0.2 : 0.1),
              border: `1px solid ${paused ? C.beacon : hexA(C.beacon, 0.4)}`,
            }}
          >
            {paused ? (
              <>
                <Play size={14} fill={C.beaconDeep} /> Resume
              </>
            ) : (
              <>
                <Pause size={14} /> Rain delay
              </>
            )}
          </button>
        </div>
      </header>

      {/* Mobile nav strip — horizontally scrollable; CSS shows it only on narrow viewports */}
      <nav
        className="mobile-nav"
        style={{
          gap: 8,
          padding: "10px 16px",
          overflowX: "auto",
          borderBottom: `1px solid ${C.hairline}`,
        }}
      >
        {NAV.map((n) => {
          const isActive = view === n.key;
          return (
            <button
              key={n.key}
              type="button"
              onClick={() => onNav(n.key)}
              aria-current={isActive ? "page" : undefined}
              style={{
                flexShrink: 0,
                padding: "8px 14px",
                borderRadius: 11,
                fontSize: 13.5,
                fontWeight: isActive ? 600 : 500,
                whiteSpace: "nowrap",
                color: isActive ? C.sea : C.textMid,
                background: isActive ? hexA(C.sea, 0.1) : "transparent",
                border: `1px solid ${isActive ? hexA(C.sea, 0.25) : "transparent"}`,
              }}
            >
              {n.label}
            </button>
          );
        })}
      </nav>
    </>
  );
}
